import { MAX_OTHER, isAnswered, type AnswerInput, type AnswerMap } from './answer.js';
import { OTHER_ID, moduleOf, type L10n, type Lang } from './common.js';
import { allQuestions, type Question, type QuestionType, type Questionnaire, type ShowIfRule } from './questionnaire.js';
import type { MemberCheckLine } from './session.js';

/**
 * The question engine. Pure functions over (questionnaire, answers, language), shared by the browser
 * (what to show next) and the API (validation, the model context, the member check). Every question id
 * with a special role comes from `questionnaire.engine`, so the consulting and overseas tracks run on
 * the same code.
 */

export interface EngineState {
  questionnaire: Questionnaire;
  answers: AnswerMap;
  lang: Lang;
}

export interface ResolvedOption {
  id: string;
  label: string;
  exclusive: boolean;
  tag?: string;
  value?: number | null;
}

export interface ResolvedPart {
  id: string;
  type: 'band' | 'single';
  label: string;
  options: ResolvedOption[];
}

export interface ResolvedQuestion {
  id: string;
  module: string;
  version: number;
  type: QuestionType;
  prompt: string;
  help?: string;
  options: ResolvedOption[];
  parts?: ResolvedPart[];
  max?: number;
  required: boolean;
  allowOther: boolean;
  allowUnsure: boolean;
  unsureLabel: string;
  otherLabel: string;
  scale?: { minLabel: string; maxLabel: string };
  explain?: string;
  chips?: string[];
  placeholder?: string;
  hint?: string;
  noNamesNote: boolean;
  core: boolean;
  optional: boolean;
}

export interface Screen {
  key: string;
  module: string;
  questionIds: string[];
}

const UNSURE: L10n = { zh: '不确定', en: 'Not sure' };
const OTHER: L10n = { zh: '其他', en: 'Other' };
const CHOICE_TYPES: QuestionType[] = ['single', 'multi', 'band', 'pick_explain', 'rank'];
/** A compound part's 其他 keeps its bank option id (C3 dept `other`); its typed text rides in `answer.other`. */
export const PART_OTHER_ID = 'other';

export const selectedOf = (answers: AnswerMap, id: string): string[] => {
  const a = answers[id];
  return a && !a.skipped ? a.selected : [];
};

/** The industry (consulting A1) or product category (overseas OA1) the variants key on. */
export const industryFor = (state: EngineState): string | undefined =>
  selectedOf(state.answers, state.questionnaire.engine.industryQuestion)[0];

/** Respondents whose role gets the plainer wording (frontline staff; sales reps on the overseas track). */
export const isPlainRole = (state: EngineState): boolean => {
  const { roleQuestion, plainRoles } = state.questionnaire.engine;
  const role = selectedOf(state.answers, roleQuestion)[0];
  return !!role && plainRoles.includes(role);
};

/** Track-agnostic shorthands for callers holding only answers (the two banks' ids never collide). */
export const industryOf = (answers: AnswerMap): string | undefined =>
  selectedOf(answers, 'A1')[0] ?? selectedOf(answers, 'OA1')[0];
export const isFrontline = (answers: AnswerMap): boolean =>
  selectedOf(answers, 'A3')[0] === 'frontline' || selectedOf(answers, 'OA4')[0] === 'sales_rep';

export function ruleHolds(rule: ShowIfRule, answers: AnswerMap): boolean {
  const sel = selectedOf(answers, rule.q);
  const values = rule.values ?? [];
  switch (rule.op) {
    case 'answered':
      return isAnswered(answers[rule.q]);
    case 'includesAny':
      return sel.some((s) => values.includes(s));
    case 'excludesAll':
      return !sel.some((s) => values.includes(s));
    case 'equals':
      return sel.length > 0 && values.includes(sel[0]!);
    case 'notEquals':
      return !(sel.length > 0 && values.includes(sel[0]!));
  }
}

const PLACEHOLDER = /\{(O?[A-I]\d+[a-z]?)\}/g;

/** {C2} (or any {questionId}) becomes the first label chosen there. Returns null when one cannot be filled. */
export function substitute(text: string, state: EngineState): string | null {
  let missing = false;
  const out = text.replace(PLACEHOLDER, (_, id: string) => {
    const label = answerLabels(state, id)[0];
    if (!label) missing = true;
    return label ?? '…';
  });
  return missing ? null : out;
}

function promptFor(q: Question, state: EngineState): string {
  const base = isPlainRole(state) && q.promptFrontline ? q.promptFrontline : q.prompt;
  const filled = substitute(base[state.lang], state);
  if (filled !== null) return filled;
  return q.promptIfMissing?.[state.lang] ?? base[state.lang].replace(PLACEHOLDER, '…');
}

function matchingVariant(q: Question, state: EngineState) {
  const industry = industryFor(state);
  return q.variants?.find((v) =>
    v.industries ? !!industry && v.industries.includes(industry) : v.when!.every((r) => ruleHolds(r, state.answers)),
  );
}

function localOptions(q: Question, state: EngineState): ResolvedOption[] {
  const variant = matchingVariant(q, state);
  let options = [...(variant?.replace ?? q.options ?? [])];
  if (variant?.add?.length) {
    const firstExclusive = options.findIndex((o) => o.exclusive);
    const at = firstExclusive === -1 ? options.length : firstExclusive;
    options = [...options.slice(0, at), ...variant.add, ...options.slice(at)];
  }
  if (variant?.order?.length) {
    const front = variant.order.map((id) => options.find((o) => o.id === id)).filter((o) => o !== undefined);
    options = [...front, ...options.filter((o) => !variant.order!.includes(o.id))];
  }
  return options.map((o) => ({
    id: o.id,
    label: o.label[state.lang],
    exclusive: !!o.exclusive,
    ...(o.tag ? { tag: o.tag } : {}),
    ...(o.value !== undefined ? { value: o.value } : {}),
  }));
}

/** C2 from C1, D2 from D1, I1 from C1 + B3: the client's own picks, namespaced "C1:copy_chat", de-duplicated. */
function derivedOptions(q: Question, state: EngineState): ResolvedOption[] {
  const out: ResolvedOption[] = [];
  const seen = new Set<string>();
  for (const srcId of q.optionsFrom!.questions) {
    const src = findIn(state.questionnaire, srcId);
    const answer = state.answers[srcId];
    if (!src || !answer || answer.skipped || !isVisible(src, state)) continue;
    const srcOptions = resolveOptions(src, state);
    for (const id of answer.selected) {
      let label: string | undefined;
      if (id === OTHER_ID) label = answer.other?.trim() || undefined;
      else {
        const opt = srcOptions.find((o) => o.id === id);
        const raw = (src.options ?? []).concat(...(src.variants ?? []).map((v) => [...(v.replace ?? []), ...(v.add ?? [])]));
        if (!opt || opt.exclusive || raw.find((o) => o.id === id)?.positive) continue;
        label = opt.label;
      }
      if (!label) continue;
      const key = label.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ id: `${srcId}:${id}`, label, exclusive: false });
    }
  }
  return q.optionsFrom!.max ? out.slice(0, q.optionsFrom!.max) : out;
}

export function resolveOptions(q: Question, state: EngineState): ResolvedOption[] {
  return q.optionsFrom ? derivedOptions(q, state) : localOptions(q, state);
}

function findIn(questionnaire: Questionnaire, id: string): Question | undefined {
  return allQuestions(questionnaire).find((q) => q.id === id);
}

export function isVisible(q: Question, state: EngineState): boolean {
  if (q.optional && state.questionnaire.hideOptional) return false;
  if (q.showIf && !q.showIf.every((r) => ruleHolds(r, state.answers))) return false;
  if (q.optionsFrom && derivedOptions(q, state).length === 0) return false;
  return true;
}

export function resolveQuestion(q: Question, state: EngineState): ResolvedQuestion {
  const lang = state.lang;
  const choice = CHOICE_TYPES.includes(q.type);
  const help = q.help ? substitute(q.help[lang], state) : null;
  return {
    id: q.id,
    module: moduleOf(q.id),
    version: q.version,
    type: q.type,
    prompt: promptFor(q, state),
    ...(help ? { help } : {}),
    options: resolveOptions(q, state),
    ...(q.parts
      ? {
          parts: q.parts.map((p) => ({
            id: p.id,
            type: p.type,
            label: p.label[lang],
            options: p.options.map((o) => ({ id: o.id, label: o.label[lang], exclusive: false, value: o.value ?? null })),
          })),
        }
      : {}),
    ...(q.max ? { max: q.max } : {}),
    required: q.required,
    // Derived lists (C2, D2, I1) already carry the client's own Other text, and rank cards cannot be "other".
    allowOther: choice && q.type !== 'rank' && !q.optionsFrom && q.allowOther,
    allowUnsure: q.allowUnsure,
    unsureLabel: (q.unsureLabel ?? UNSURE)[lang],
    otherLabel: OTHER[lang],
    ...(q.scale ? { scale: { minLabel: q.scale.minLabel[lang], maxLabel: q.scale.maxLabel[lang] } } : {}),
    ...(q.explain ? { explain: q.explain.prompt[lang] } : {}),
    ...(q.chips ? { chips: q.chips.map((c) => c[lang]) } : {}),
    ...(q.placeholder ? { placeholder: q.placeholder[lang] } : {}),
    ...(q.hint ? { hint: q.hint[lang] } : {}),
    noNamesNote: !!q.noNamesNote,
    core: q.core,
    optional: !!q.optional,
  };
}

export function visibleQuestions(state: EngineState): Question[] {
  return allQuestions(state.questionnaire).filter((q) => isVisible(q, state));
}

/** One question per screen, except questions sharing a `screen` key (the three profile chip rows). */
export function screens(state: EngineState): Screen[] {
  const out: Screen[] = [];
  for (const q of visibleQuestions(state)) {
    const key = q.screen ?? q.id;
    const last = out[out.length - 1];
    if (last && last.key === key) last.questionIds.push(q.id);
    else out.push({ key, module: moduleOf(q.id), questionIds: [q.id] });
  }
  return out;
}

/**
 * The short textual cue under the module name ("还有 3 个小问题") counts screens left in the module. A
 * derived question (C2, OE2, OF2) waiting on picks made on this or a later screen counts as coming, so
 * the cue does not stall when it appears.
 */
export function screensLeftInModule(state: EngineState, screenKey: string): number {
  const all = screens(state);
  const i = all.findIndex((s) => s.key === screenKey);
  if (i === -1) return 0;
  const module = all[i]!.module;
  const shown = new Set(all.flatMap((s) => s.questionIds));
  const done = new Set(all.slice(0, i).flatMap((s) => s.questionIds));
  const pending = allQuestions(state.questionnaire).filter(
    (q) =>
      moduleOf(q.id) === module &&
      !shown.has(q.id) &&
      !!q.optionsFrom &&
      (!q.showIf || q.showIf.every((r) => ruleHolds(r, state.answers))) &&
      q.optionsFrom.questions.some((src) => !done.has(src) && shown.has(src)),
  );
  return all.slice(i + 1).filter((s) => s.module === module).length + pending.length;
}

export interface Completion {
  coreTotal: number;
  coreAnswered: number;
  ratio: number;
  /** Fewer than 60% of core questions answered: a shorter brief and overallConfidence forced to low. */
  sparse: boolean;
}

export const SPARSE_THRESHOLD = 0.6;

export function completion(state: EngineState): Completion {
  const eff = effectiveState(state);
  const core = visibleQuestions(eff).filter((q) => q.core);
  const answered = core.filter((q) => isAnswered(eff.answers[q.id])).length;
  const ratio = core.length ? answered / core.length : 0;
  return { coreTotal: core.length, coreAnswered: answered, ratio, sparse: ratio < SPARSE_THRESHOLD };
}

/** Hidden by a branch, but the branch means "none" (overseas: not started → no inquiries, no outreach). */
export function zeroedQuestions(state: EngineState): string[] {
  const eff = effectiveState(state);
  return state.questionnaire.engine.zeroWhenHidden.filter((id) => {
    const q = findIn(state.questionnaire, id);
    return !!q && !isVisible(q, eff);
  });
}

/**
 * The answers as they stand now: answers to questions a later change has hidden are dropped, and picks
 * that a variant or a derived list no longer offers are removed (an answer left empty by that is dropped
 * too). Worked through in bank order, so chains such as C1 → C2 → I1 resolve. Everything that reads
 * answers for the client, the brief or the model should read this, not the raw map.
 */
export function effectiveState(state: EngineState): EngineState {
  const answers: AnswerMap = {};
  const eff: EngineState = { ...state, answers };
  for (const q of allQuestions(state.questionnaire)) {
    const raw = state.answers[q.id];
    if (!raw || !isVisible(q, eff)) continue;
    if (raw.skipped) {
      answers[q.id] = raw;
      continue;
    }
    if (q.type === 'contact' || q.type === 'checkbox') {
      answers[q.id] = raw;
      continue;
    }
    const clean = normaliseAnswer(resolveQuestion(q, eff), raw);
    if (isAnswered(clean)) answers[q.id] = { ...raw, ...clean };
  }
  return eff;
}

/** The labels a respondent chose, in the session language. Other becomes their own words. */
export function answerLabels(state: EngineState, questionId: string): string[] {
  const q = findIn(state.questionnaire, questionId);
  const a = state.answers[questionId];
  if (!q || !a || a.skipped) return [];
  if (a.unsure && !a.selected.length) return [(q.unsureLabel ?? UNSURE)[state.lang]];
  const options = resolveOptions(q, state);
  const labels: string[] = [];
  const ids = q.type === 'rank' && a.rank?.length ? a.rank : a.selected;
  for (const id of ids) {
    if (id === OTHER_ID) {
      labels.push(a.other?.trim() || OTHER[state.lang]);
      continue;
    }
    const opt = options.find((o) => o.id === id);
    if (opt) labels.push(opt.label);
  }
  if (q.parts && a.parts) {
    for (const part of q.parts) {
      // A part's 其他 is a typed field too (C3 department): the respondent's words stand in for the label.
      if (a.parts[part.id] === PART_OTHER_ID && a.other?.trim()) {
        labels.push(a.other.trim());
        continue;
      }
      const opt = part.options.find((o) => o.id === a.parts![part.id]);
      if (opt) labels.push(opt.label[state.lang]);
    }
  }
  return labels;
}

/** The top pain's size: people band midpoint × hours band midpoint. null when unknown, 0 when zeroed by a branch. */
export function peopleHoursPerWeek(state: EngineState): number | null {
  const id = state.questionnaire.engine.magnitudeQuestion;
  if (zeroedQuestions(state).includes(id)) return 0;
  const q = findIn(state.questionnaire, id);
  const a = effectiveState(state).answers[id];
  if (!q?.parts || !a?.parts || a.skipped) return null;
  const valueOf = (partId: string) =>
    q.parts!.find((p) => p.id === partId)?.options.find((o) => o.id === a.parts![partId])?.value ?? null;
  const people = valueOf('people');
  const hours = valueOf('hours');
  if (people === null || hours === null) return null;
  return Math.round(people * hours);
}

const join = (labels: string[], lang: Lang) => labels.join(lang === 'zh' ? '、' : ', ');

/** Default wording; a questionnaire's engine.memberCheck entry can override it with `template`. */
const MEMBER_CHECK: Record<MemberCheckLine['key'], L10n> = {
  pain: { zh: '最耗费人力的事项：{x}', en: 'What costs the most people-time: {x}' },
  size: { zh: '大致规模：{x}', en: 'Roughly how big it is: {x}' },
  consequence: { zh: '出现差错或延误时的主要后果：{x}', en: 'What it costs when it goes wrong or runs late: {x}' },
  done: { zh: '最希望优先实现的改变：{x}', en: 'The first change you want to see: {x}' },
  constraint: { zh: '数据与系统方面的硬性要求：{x}', en: 'Hard requirements on data and systems: {x}' },
  markets: { zh: '计划进入的市场：{x}', en: 'The markets you want to enter: {x}' },
};

/** Chinese puts a space between a character and a following number or Latin word ("每人每周 5–10 小时"). */
const zhJoin = (a: string, b: string) => (/^[0-9A-Za-z]/.test(b) ? `${a} ${b}` : `${a}${b}`);
/** A bank label used mid-sentence in English: "Over 10" reads "over 10" ("Admin and HR" keeps its HR, "IT team" its IT). */
const lc = (s: string) => (/^[A-Z]{2}/.test(s) ? s : s.charAt(0).toLowerCase() + s.slice(1));

/** The size line reads the magnitude question's parts: people, hours, and a department or a sales cycle. */
function sizeLine(state: EngineState, source: string): string | null {
  const q = findIn(state.questionnaire, source);
  const a = state.answers[source];
  if (!q?.parts || !a?.parts || a.skipped) return null;
  const option = (partId: string) => q.parts!.find((p) => p.id === partId)?.options.find((o) => o.id === a.parts![partId]);
  const label = (partId: string) => option(partId)?.label[state.lang];
  // A band answered "hard to estimate" (value null) is named as such, not used as a count ("人数难以估计").
  const unknown = (partId: string) => option(partId)?.value === null;
  const people = label('people');
  const hours = label('hours');
  // "Other" is the department the respondent typed (kept as typed); without text it says nothing.
  const typedDept = a.parts['dept'] === PART_OTHER_ID ? a.other?.trim() || undefined : undefined;
  const dept = a.parts['dept'] === PART_OTHER_ID ? typedDept : label('dept');
  const cycle = label('cycle');
  const cycleUnsure = a.parts['cycle'] === 'not_sure';
  const bits =
    state.lang === 'zh'
      ? [
          people && (unknown('people') ? `人数${people}` : `${people}负责`),
          hours && zhJoin(unknown('hours') ? '每人每周用时' : '每人每周', hours),
          dept && `${zhJoin('主要由', dept)}${/[0-9A-Za-z]$/.test(dept) ? ' ' : ''}承担`,
          cycle && zhJoin('成交周期', cycle),
        ]
      : [
          people &&
            (unknown('people')
              ? `number of people: ${people.toLowerCase()}`
              : /\b(people|person)$/i.test(people)
                ? lc(people)
                : `${lc(people)} people`),
          hours && (unknown('hours') ? `hours each: ${hours.toLowerCase()}` : `${lc(hours)} hours a week each`),
          dept && `mostly handled by ${typedDept ?? lc(dept)}`,
          cycle && (cycleUnsure ? `sales cycle: ${cycle.toLowerCase()}` : `a sales cycle of ${lc(cycle)}`),
        ];
  const parts = bits.filter((b): b is string => !!b);
  return parts.length ? parts.join(state.lang === 'zh' ? '，' : ', ') : null;
}

/** 我们听到的是: templated, not written by the model, so the screen costs no latency. */
export function memberCheckLines(state: EngineState): MemberCheckLine[] {
  const eff = effectiveState(state);
  const lines: MemberCheckLine[] = [];
  for (const { key, source, template } of state.questionnaire.engine.memberCheck) {
    const value = key === 'size' ? sizeLine(eff, source) : join(answerLabels(eff, source), state.lang) || null;
    if (!value) continue;
    lines.push({ key, source, text: (template ?? MEMBER_CHECK[key])[state.lang].replace('{x}', value), confirmed: null });
  }
  return lines;
}

/**
 * Normalises an answer to what the question as resolved for this session can hold: picks the options no
 * longer offer (after a variant or a derived list changed) are dropped, exclusive picks clear the rest,
 * single types keep one, and fields the type does not use are removed (so text typed into a choice
 * question cannot make a required question count as answered). Text is kept exactly as typed: this runs
 * on every keystroke in the browser, and emptiness checks trim on their own.
 */
export function normaliseAnswer(rq: ResolvedQuestion, input: AnswerInput): AnswerInput {
  const elapsed = input.elapsedMs !== undefined ? { elapsedMs: input.elapsedMs } : {};
  if (input.skipped) return { selected: [], unsure: false, skipped: true, ...elapsed };
  if (input.unsure && rq.allowUnsure) return { selected: [], unsure: true, skipped: false, ...elapsed };
  const base = { selected: [] as string[], unsure: false, skipped: false, ...elapsed };
  const offered = new Set(rq.options.map((o) => o.id));
  if (rq.allowOther) offered.add(OTHER_ID);
  switch (rq.type) {
    case 'scale':
      return { ...base, ...(input.scale !== undefined ? { scale: input.scale } : {}) };
    case 'text':
      return { ...base, ...(input.text !== undefined ? { text: input.text } : {}) };
    case 'rank': {
      const rank = [...new Set(input.rank ?? [])].filter((id) => offered.has(id)).slice(0, 3);
      return { ...base, rank };
    }
    case 'compound': {
      const parts: Record<string, string> = {};
      for (const [partId, optId] of Object.entries(input.parts ?? {})) {
        if (rq.parts?.find((p) => p.id === partId)?.options.some((o) => o.id === optId)) parts[partId] = optId;
      }
      // A part's 其他 (C3 department) is typed into directly: its text rides in `other`, as on a choice question.
      const typed = Object.values(parts).includes(PART_OTHER_ID) && input.other?.trim();
      return { ...base, parts, ...(typed ? { other: input.other!.slice(0, MAX_OTHER) } : {}) };
    }
    case 'contact':
    case 'checkbox':
      return { ...input };
    default: {
      let selected = [...new Set(input.selected)].filter((id) => offered.has(id));
      const exclusive = selected.find((id) => rq.options.find((o) => o.id === id)?.exclusive);
      if (exclusive) selected = [exclusive];
      if (['single', 'band'].includes(rq.type)) selected = selected.slice(-1);
      return {
        ...base,
        selected,
        ...(selected.includes(OTHER_ID) && input.other !== undefined ? { other: input.other } : {}),
        ...(rq.explain && input.text !== undefined ? { text: input.text } : {}),
      };
    }
  }
}

/** Returns an error message, or null when the answer fits the question as resolved for this session. */
export function validateAnswer(rq: ResolvedQuestion, input: AnswerInput): string | null {
  if (input.skipped) return rq.required ? `${rq.id} is required` : null;
  if (input.unsure && !rq.allowUnsure) return `${rq.id} does not offer "not sure"`;
  const optionIds = new Set(rq.options.map((o) => o.id));
  if (rq.allowOther) optionIds.add(OTHER_ID);
  for (const id of input.selected) {
    if (!optionIds.has(id)) return `${rq.id}: unknown option ${id}`;
  }
  if (['single', 'band'].includes(rq.type) && input.selected.length > 1) return `${rq.id} takes one choice`;
  if (rq.max && input.selected.length > rq.max) return `${rq.id} takes at most ${rq.max}`;
  if (rq.type === 'scale' && input.scale === undefined && !input.unsure) return `${rq.id} needs a value from 1 to 5`;
  if (rq.type === 'rank' && input.rank) {
    if (new Set(input.rank).size !== input.rank.length) return `${rq.id}: rank has duplicates`;
    for (const id of input.rank) if (!optionIds.has(id)) return `${rq.id}: unknown card ${id}`;
  }
  if (rq.type === 'compound' && input.parts) {
    for (const [partId, optId] of Object.entries(input.parts)) {
      const part = rq.parts?.find((p) => p.id === partId);
      if (!part) return `${rq.id}: unknown part ${partId}`;
      if (!part.options.some((o) => o.id === optId)) return `${rq.id}: unknown option ${optId} in ${partId}`;
    }
  }
  if (rq.required && !isAnswered(input)) return `${rq.id} is required`;
  return null;
}
